import React from 'react';
import { Plus, Trash2, CheckCircle } from 'lucide-react';
import { WhyStep } from '../../types/rca.types';

interface FiveWhyStepsProps {
    whySteps: WhyStep[];
    addWhyLevel: () => void;
    removeWhyLevel: (index: number) => void;
    updateAnswer: (index: number, answer: string) => void;
}

export const FiveWhySteps: React.FC<FiveWhyStepsProps> = ({
    whySteps,
    addWhyLevel,
    removeWhyLevel,
    updateAnswer
}) => {
    return (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">The 5 Whys</h2>
                {whySteps.length < 5 && (
                    <button
                        onClick={addWhyLevel}
                        className="flex items-center gap-2 px-3 py-1 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        <Plus className="w-4 h-4" />
                        Add Why Level
                    </button>
                )}
            </div>

            <div className="space-y-4">
                {whySteps.map((step, index) => (
                    <div key={index} className="relative">
                        <div className="flex items-start gap-4">
                            <div className="flex flex-col items-center">
                                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center font-semibold text-blue-700">
                                    {step.level}
                                </div>
                                {index < whySteps.length - 1 && (
                                    <div className="w-0.5 h-full bg-blue-200 mt-2"></div>
                                )}
                            </div>

                            <div className="flex-1">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    {step.question}
                                </label>
                                <div className="flex gap-2">
                                    <textarea
                                        value={step.answer}
                                        onChange={(e) => updateAnswer(index, e.target.value)}
                                        rows={2}
                                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="Enter your answer..."
                                    />
                                    {whySteps.length > 1 && (
                                        <button
                                            onClick={() => removeWhyLevel(index)}
                                            className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {whySteps.length < 5 && (
                <p className="mt-4 text-sm text-gray-500 italic">
                    💡 Tip: Keep asking "Why?" until you reach a root cause that is actionable and within your control
                </p>
            )}

            {whySteps.length === 5 && (
                <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <p className="text-sm text-green-800">
                        You've reached 5 levels! Now identify the actionable root cause below.
                    </p>
                </div>
            )}
        </div>
    );
};
